import posthog from 'posthog-js';
import type {PostHogConfig} from 'posthog-js';
import type {PostHogIdentity} from '~/lib/auth/token-store';

type LoadedPostHog = Parameters<NonNullable<PostHogConfig['loaded']>>[0];

// Long enough for one /flags round trip, short enough that a blocked or slow
// PostHog never visibly holds the page.
const FLAGS_READY_TIMEOUT_MS = 1500;

let initialized = false;

export function initializeAnalytics() {
  if (initialized) {
    return posthog;
  }

  const apiKey = import.meta.env.DEV
    ? import.meta.env.POSTHOG_KEY_DEV
    : import.meta.env.POSTHOG_KEY_PROD;

  if (!apiKey) {
    return posthog;
  }

  posthog.init(apiKey, {
    api_host: import.meta.env.POSTHOG_HOST,
    person_profiles: 'identified_only',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: import.meta.env.PROD,
    // Flags are targeted at the viewer's organization group, so an anonymous
    // evaluation is wasted and its late response would race the identified one.
    advanced_disable_feature_flags_on_first_load: true,
    loaded: applyLocalFeatureFlagOverrides,
  });

  initialized = true;
  return posthog;
}

// Identifies the viewer and resolves once flags reflect that identity, or
// after the timeout. Resolves immediately when analytics is not running.
export function identifyExternalUser(identity: PostHogIdentity): Promise<void> {
  if (!initialized) {
    return Promise.resolve();
  }

  posthog.register({
    country: identity.region,
  });

  if (typeof identity.is_org_member_viewer === 'boolean') {
    posthog.register({is_org_member_viewer: identity.is_org_member_viewer});
  } else {
    posthog.unregister('is_org_member_viewer');
  }

  posthog.identify(identity.distinct_id);
  posthog.group('organization', identity.group_key);
  // identify/group only reload flags when the id or group changed, so a
  // returning viewer would otherwise never load them (first-load fetch is off).
  posthog.reloadFeatureFlags();

  return waitForFeatureFlags(FLAGS_READY_TIMEOUT_MS);
}

function waitForFeatureFlags(timeoutMs: number): Promise<void> {
  let unsubscribe = () => {};
  const settled = new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    unsubscribe = posthog.onFeatureFlags(() => {
      clearTimeout(timer);
      resolve();
    });
  });
  return settled.finally(() => unsubscribe());
}

function applyLocalFeatureFlagOverrides(ph: LoadedPostHog) {
  if (!import.meta.env.DEV) {
    return;
  }

  const flags: Record<string, boolean> = {};
  for (const key of parseFlagList(import.meta.env.VITE_ENABLED_FEATURE_FLAGS)) {
    flags[key] = true;
  }
  for (const key of parseFlagList(
    import.meta.env.VITE_DISABLED_FEATURE_FLAGS,
  )) {
    flags[key] = false;
  }

  if (Object.keys(flags).length) {
    ph.featureFlags.overrideFeatureFlags({flags, suppressWarning: true});
  }
}

function parseFlagList(raw: string | undefined): string[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((flag) => flag.trim())
    .filter(Boolean);
}

export {posthog};
